import type { ReactNode } from "react";
import { BittensorIcon, ContourCanvas } from "@soma/ui";
import { cn } from "@/lib/utils";

/**
 * The heading of every dashboard page: the contour rings of the research band on the home
 * page, in the page's own theme, over a hairline that separates it from what follows.
 */
export function Band({ children }: { children: ReactNode }) {
  return (
    <section className="relative overflow-hidden border-b border-line">
      {/* On white the rings read heavier than on the dark theme, so light mode gets them
          at half strength. */}
      <ContourCanvas
        variant="page"
        className="top-0 -right-1/2 h-full w-[130%] opacity-30 lg:-right-[12%] lg:w-[70%] lg:opacity-55 dark:opacity-40 dark:lg:opacity-100"
      />
      <div className="wrap relative z-10 py-[clamp(40px,6vw,72px)]">{children}</div>
    </section>
  );
}

export interface StripItem {
  id: string;
  label: string;
  value: ReactNode;
  /** A second, quieter line under the value. */
  note?: ReactNode;
  /** An amount in TAO: Bittensor's mark goes in front of the figure, as a unit. */
  tao?: boolean;
}

/**
 * Figures in a row, label above value - the research band's stat strip. A grid rather
 * than a wrapping row, so four figures fold into two by two, never three and one; the
 * 1px gap over the line colour draws the rules between cells in both layouts.
 */
export function StatStrip({ items, className }: { items: StripItem[]; className?: string }) {
  return (
    <dl
      className={cn(
        "grid grid-cols-2 gap-px overflow-hidden rounded-[14px] border border-line bg-line md:grid-cols-4",
        className,
      )}
    >
      {items.map((s) => (
        <div key={s.id} className="min-w-0 bg-bg px-6 py-5">
          <dt className="eyebrow text-[10.5px] tracking-[0.12em] text-fg-muted">{s.label}</dt>
          <dd className="mt-2.5 flex items-center gap-1.5 text-[26px] leading-none font-medium tracking-[-0.02em] whitespace-nowrap">
            {/* A unit, not a logo: smaller than the digits so the figure leads. */}
            {s.tao && <BittensorIcon className="h-[14px] w-auto shrink-0 text-accent" />}
            {s.value}
          </dd>
          {s.note && <dd className="mt-1.5 truncate text-[12px] text-fg-muted">{s.note}</dd>}
        </div>
      ))}
    </dl>
  );
}
